/**
 * Domain types for the `iq-data-source` entity slice — the file-upload sources an IQ report
 * can draw on (YouGov, Pathmatics), activated per workspace from Knowledge.
 *
 * Distinct from `entities/media-connector`, which models OAuth ad-platform accounts. These
 * share the Knowledge surface with those, and nothing else: no account, no credentials,
 * nothing to sync.
 */
import type { DataSourceDto } from '@inference.engine/api-client/web';
import { i18n } from '@/shared/i18n';

export type IqDataSource = DataSourceDto['source'];

export type IqDataSourceConnection = DataSourceDto;

export const IQ_DATA_SOURCE_ORDER: readonly IqDataSource[] = ['pathmatics', 'yougov'] as const;

// Getters, not values: resolving a label to a plain string at import freezes its copy before
// i18n is ready. Mirrors the connector label tables in `entities/media-connector`.
export const IQ_DATA_SOURCE_LABELS: Record<IqDataSource, string> = {
  get yougov() {
    return i18n.t('knowledge:dataSource.yougov.label');
  },
  get pathmatics() {
    return i18n.t('knowledge:dataSource.pathmatics.label');
  },
};

export const IQ_DATA_SOURCE_DESCRIPTIONS: Record<IqDataSource, string> = {
  get yougov() {
    return i18n.t('knowledge:dataSource.yougov.description');
  },
  get pathmatics() {
    return i18n.t('knowledge:dataSource.pathmatics.description');
  },
};

export const IQ_DATA_SOURCE_CATEGORIES: Record<IqDataSource, string> = {
  get yougov() {
    return i18n.t('knowledge:dataSource.yougov.category');
  },
  get pathmatics() {
    return i18n.t('knowledge:dataSource.pathmatics.category');
  },
};

/** What the upload panel says the file will produce, per source. */
export const IQ_DATA_SOURCE_UPLOAD_HINTS: Record<IqDataSource, string> = {
  get yougov() {
    return i18n.t('knowledge:dataSource.yougov.uploadHint');
  },
  get pathmatics() {
    return i18n.t('knowledge:dataSource.pathmatics.uploadHint');
  },
};

/** Single-letter mark shown in place of a logo — these vendors ship us no brand asset. */
export const IQ_DATA_SOURCE_MARKS: Record<IqDataSource, string> = {
  yougov: 'Y',
  pathmatics: 'P',
};
